/**
 * captureConstraints — single source of truth for the voice getUserMedia audio
 * constraints, plus the Issue #1350 / #1516 Win32-gated AEC capture experiment.
 *
 * WHY (issue #1350, 2026-06-08 comment "resampler refuted"):
 *   The dominant remaining Win32 voice failure is the "signal-suppressed"
 *   signature — mic provably sending (outboundChunksSent > 50), Gemini alive
 *   (outputTranscriptionEvents growing), yet inputTranscriptionEvents == 0.
 *   The resampler was refuted (capture is already ~16 kHz). The leading
 *   mechanism is Chrome's native Windows echo canceller ("Voice Capture DSP"),
 *   which Google itself flags as unreliable on Windows and which only runs while
 *   the far-end (AI) is playing — matching every failing run (AI actively
 *   talking). On laptop speakers it can over-cancel the learner's own voice →
 *   right-rate-but-gutted audio → ASR deaf. Win32 ≈ 15% zero-tx; iPhone 0%.
 *
 * EXPERIMENT:
 *   Disable echoCancellation on Win32 ONLY, behind a default-OFF build flag
 *   `VITE_VOICE_WIN32_DISABLE_AEC`, A/B'd against the Int16-clamp baseline
 *   (worklets/audio-processing.ts, PR #1502). The gate is
 *   `navigator.platform === 'Win32'` — exactly the diagnostics bucket the issue
 *   thread keys on — so the **iOS three-layer audio fix is never touched**
 *   (docs/subsystems/voice-mode.md gotcha #1): off-Win32, output is byte-for-byte
 *   the historical constraints.
 *
 * RUNTIME A/B OVERRIDE (for the #1516 VM experiment):
 *   A build-time flag can't be A/B'd in one staging session — Vite inlines it.
 *   `?aec=on|off|clear` forces either arm within a single build so a tester can
 *   compare back-to-back. The chosen arm persists to sessionStorage so it
 *   survives the in-tab navigations the chat flow performs (landing → /chat/:id).
 *   No real learner ever sets this param, so production behavior is unaffected.
 *   Precedence: explicit override > (Win32 && build flag).
 *
 * The active arm is exposed via `aecModeLabel()` and stamped into
 * voice_diagnostics (`metadata.aecMode`) so the result is legible on the
 * /admin/voice-cic page and in post-hoc SQL.
 *
 * Read docs/subsystems/voice-mode.md gotcha #19 before touching this file.
 */

const AEC_OVERRIDE_KEY = 'voice.aecOverride';

/**
 * Win32 gate. `navigator.platform === 'Win32'` is frozen on 64-bit Windows for
 * web-compat and is precisely the `voice_diagnostics.platform` value the #1350
 * thread buckets on ("Win32" rows). Never throws.
 */
export function isWin32Platform() {
  try {
    return typeof navigator !== 'undefined' && navigator.platform === 'Win32';
  } catch (_e) {
    return false;
  }
}

/** Build flag: VITE_VOICE_WIN32_DISABLE_AEC === 'true' (default OFF). */
function buildFlagDisablesAec() {
  try {
    return (
      typeof import.meta !== 'undefined' &&
      import.meta.env &&
      String(import.meta.env.VITE_VOICE_WIN32_DISABLE_AEC).toLowerCase() === 'true'
    );
  } catch (_e) {
    return false;
  }
}

/**
 * Read + persist the runtime A/B override. An explicit `?aec=` param in the
 * current URL wins and is written to sessionStorage; otherwise the last
 * persisted value (same tab) is used. `?aec=clear` removes it.
 *
 * @returns {'on'|'off'|null} 'on' = force AEC (baseline arm),
 *                            'off' = force-disable AEC (experiment arm),
 *                            null = no override (fall through to Win32+flag).
 */
export function readAecOverride() {
  // 1. Parse the explicit URL intent — storage-independent, so a tester's
  //    ?aec= param is honoured even when sessionStorage is unavailable/throws.
  let urlIntent = null; // 'on' | 'off' | 'clear' | null
  try {
    if (typeof window !== 'undefined' && window.location && window.location.search) {
      const v = new URLSearchParams(window.location.search).get('aec');
      if (v != null) {
        const s = String(v).toLowerCase();
        if (s === 'on' || s === 'true' || s === '1') urlIntent = 'on';
        else if (s === 'off' || s === 'false' || s === '0') urlIntent = 'off';
        else if (s === 'clear') urlIntent = 'clear';
      }
    }
  } catch (_e) {
    /* URL unavailable — fall through to stored value */
  }

  // 2. Best-effort persist + read. Persisting lets the arm survive the in-tab
  //    navigations the chat flow performs.
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage !== null) {
      if (urlIntent === 'clear') {
        sessionStorage.removeItem(AEC_OVERRIDE_KEY);
        return null;
      }
      if (urlIntent === 'on' || urlIntent === 'off') {
        sessionStorage.setItem(AEC_OVERRIDE_KEY, urlIntent);
        return urlIntent;
      }
      const stored = sessionStorage.getItem(AEC_OVERRIDE_KEY);
      if (stored === 'on' || stored === 'off') return stored;
      return null;
    }
  } catch (_e) {
    /* storage threw (private browsing / quota) — fall back to the URL intent */
  }

  // 3. No usable storage: honour the URL intent for this call only.
  if (urlIntent === 'on' || urlIntent === 'off') return urlIntent;
  return null;
}

/**
 * True when echoCancellation should be DISABLED for this session.
 * Explicit tester override wins; otherwise the experiment fires only on
 * Win32 with the build flag ON.
 */
export function isAecDisabled() {
  const override = readAecOverride();
  if (override === 'on') return false; // baseline arm forced (AEC kept on)
  if (override === 'off') return true; // experiment arm forced (AEC off)
  return isWin32Platform() && buildFlagDisablesAec();
}

/** Stable label for telemetry/console: 'disabled' | 'enabled'. */
export function aecModeLabel() {
  return isAecDisabled() ? 'disabled' : 'enabled';
}

// Issue #2084: the microphone device the learner explicitly picked and
// verified via MicDataProbeFailedModal's device picker (`getUserMedia`
// resolved on it). Module-level, not a React ref, so it is read
// automatically by buildAudioCaptureConstraints() below — the single
// source of truth shared by ALL THREE capture sites (requestMicrophoneAccess,
// the iOS full-duplex early stream, AudioRecorder's fallback) — with no
// change needed at any of those call sites: whichever one opens the next
// live mic stream picks up the learner's choice. Root cause this closes
// (#2084 review-fix): the picker previously verified the device then called
// a bare `onRetry()`, so the reconnect's getUserMedia had no way to know
// which device was chosen and silently re-opened the default (the same
// dead device that failed the probe).
let preferredMicDeviceId = null;

/** Record the learner's verified device choice. Falsy/non-string clears it. */
export function setPreferredMicDeviceId(deviceId) {
  preferredMicDeviceId = typeof deviceId === 'string' && deviceId ? deviceId : null;
}

/**
 * Fail-open escape hatch: a picked device can go stale between selection and
 * the next getUserMedia call (unplugged, OS demotion) — the caller clears the
 * preference and retries against the default rather than blocking reconnect.
 */
export function clearPreferredMicDeviceId() {
  preferredMicDeviceId = null;
}

/** Current preferred device id, or null. */
export function getPreferredMicDeviceId() {
  return preferredMicDeviceId;
}

/**
 * The `audio` half of a getUserMedia constraints object. Byte-identical to the
 * historical inline constraints EXCEPT echoCancellation, which flips to false
 * only in the experiment arm, and `deviceId`, which is present only once a
 * device has been explicitly picked (#2084 — see preferredMicDeviceId above).
 * Used at ALL THREE capture sites (requestMicrophoneAccess, the iOS
 * full-duplex early stream, and AudioRecorder) so whichever opens the live
 * mic stream applies the same arm AND the same device choice.
 *
 * @param {string} [deviceId] - Explicit override; when omitted, falls back to
 *   the module-level preferred device (if any).
 */
export function buildAudioCaptureConstraints(deviceId) {
  const effectiveDeviceId = deviceId !== undefined ? deviceId : preferredMicDeviceId;
  const base = {
    sampleRate: 16000,
    channelCount: 1,
    noiseSuppression: true,
    echoCancellation: !isAecDisabled(),
    autoGainControl: true,
  };
  if (effectiveDeviceId) {
    return { ...base, deviceId: { exact: effectiveDeviceId } };
  }
  return base;
}
